import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';

/**
 * PermissionRouteGuard checks if the authenticated user has permission to view or mutate
 * a given route. If the user is in a read-only role (e.g. Viewer) or lacks write permissions,
 * it prevents access to /new or /edit pages and redirects to the safe fallback path.
 */
export default function PermissionRouteGuard({
  resource,
  action = 'create',
  fallbackPath = '/dashboard',
  children
}) {
  const { isSuperAdmin, hasPermission, isViewOnly } = useAuth();
  const location = useLocation();

  if (isSuperAdmin) {
    return children;
  }

  // If this is a mutation route (create or update) and user is in View-Only mode (e.g. Viewer)
  if ((action === 'create' || action === 'update' || action === 'delete') && isViewOnly) {
    toast.error(`Access Denied: You have Read-Only access (Viewer role). Cannot ${action} ${resource}.`, {
      id: `denied-${resource}-${action}`,
      duration: 4000
    });
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  // Check specific RBAC permission
  const requiredPermission = `${resource}:${action}`;
  if (resource && action && !hasPermission(requiredPermission)) {
    toast.error(`Access Denied: Missing permission '${requiredPermission}'.`, {
      id: `missing-${requiredPermission}`,
      duration: 4000
    });
    return <Navigate to={fallbackPath} state={{ from: location }} replace />;
  }

  return children;
}
